import { Fragment, useEffect, useRef, useState } from "react";
import { isNonHeaderCsvError } from "./common/csvErrors";

// ui
import {
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ★cmd-po_1142: PoModalShell + PoForm 乗せ替え
import { PoModalShell } from "./common/PoModalShell";
import { PoButton } from "./common/PoButton";
import { PoField, PoLabel, PoHelper, poAutocompleteSx } from "./common/PoForm";

// API
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

import { getSampleCSV } from "../api/presignedURL";
import { parseCSVForPreview } from "../lib/csvPreview";

// CSVカラム
const putCSVColumnContractNumber = [
  "申込番号",
  "支払方法",
  "リストID",
  "申込日",
  "開通日",
  "解約日",
  "ステータス",
  "顧客・契約情報",
  "取込を行わない",
];
const putCSVColumnPhoneNumber = [
  "顧客電話番号",
  "支払方法",
  "リストID",
  "申込日",
  "開通日",
  "解約日",
  "ステータス",
  "顧客・契約情報",
  "取込を行わない",
];

// CSVカラム（結果）
const putCSVColumnResultContractNumber = [
  "申込番号",
  "支払方法",
  "リストID",
  "申込日",
  "開通日",
  "解約日",
  "ステータス",
];
const putCSVColumnResultPhoneNumber = [
  "顧客電話番号",
  "支払方法",
  "リストID",
  "申込日",
  "開通日",
  "解約日",
  "ステータス",
];

interface PostCSVContractStatusModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  postCSVStatus: {
    status:
      | "none"
      | "open"
      | "column"
      | "posting"
      | "result"
      | "loading"
      | "error";
    message: string;
    result: { [key: string]: any }[];
    header_location: string;
    destination_key: string;
    product_id: null | number;
    product_key: string;
    header_inf: {
      [key: string]: string;
    };
    error_count: number | null;
    total_count: number | null;
    success_count: number | null;
  };
  setPostCSVStatus: (status: {
    status:
      | "none"
      | "open"
      | "column"
      | "posting"
      | "result"
      | "loading"
      | "error";
    message: string;
    result: { [key: string]: any }[];
    header_location: string;
    destination_key: string;
    product_id: null | number;
    product_key: string;
    header_inf: {
      [key: string]: string;
    };
    error_count: number | null;
    total_count: number | null;
    success_count: number | null;
  }) => void;
  postCSVFunc: (postDatas: File) => void;
  startPutCSVFunc: () => void;
  postCSVTitle: string;
  productList: {
    id: number;
    high_rank_company_id: number;
    product_name: string;
    high_rank_company_name: string;
    product_key: string;
  }[];
  postCSVStatusHeader: string[];
}

export function PostCSVContractStatusModal(
  props: PostCSVContractStatusModalProps
) {
  const {
    onModal,
    swCloseModal,
    postCSVStatus,
    setPostCSVStatus,
    postCSVFunc,
    startPutCSVFunc,
    postCSVTitle,
    productList,
    postCSVStatusHeader,
  } = props;
  async function handleFileChange(
    event_file: React.ChangeEvent<HTMLInputElement>
  ) {
    if (event_file.target.files && event_file.target.files.length > 0) {
      const file = event_file.target.files[0];
      // ★cmd-po_1232 R33: 確認stepを廃止。ファイル選択で直接 column 面へ進む。
      // データ行0のCSVは選択面に留めてエラー表示（旧「取込実行」disabled 床の移設）。
      const { rowCount } = await parseCSVForPreview(file);
      if (rowCount === 0) {
        setFileError("CSVにデータ行がありません。取込を実行できません。");
        event_file.target.files = null;
        return;
      }
      setFileError("");
      // ★cmd-po_1232 R33: 総件数snapshotの移設（旧「取込実行」で捕捉していた値をここで確定）。
      rowCountSnapshotRef.current = rowCount;
      postCSVFunc(file);
    }
    event_file.target.files = null;
  }
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  // ★cmd-po_1142: const style 削除 → PoModalShell widthPx={dialogWidth} で担当
  const dialogWidth =
    postCSVStatus.status === "column"
      ? "640px"
      : postCSVStatus.status === "open"
      ? "480px"
      : !isSmartPhone ? "1200px" : "calc(100% - 82px)";

  // ★cmd-po_1232 R33: 確認step廃止に伴い preview* state を撤去。選択面のエラー表示のみ残す。
  const [fileError, setFileError] = useState<string>("");
  const rowCountSnapshotRef = useRef<number>(0);

  const [postCSVColumn, setPostCSVColumn] = useState<string[]>([]);
  const [postCSVColumnResult, setPostCSVColumnResult] = useState<string[]>([]);

  const [postCSVProductName, setPostCSVProductName] = useState<string>("");
  const autocompleteProductList: string[] = productList.map((product) => {
    return product.high_rank_company_name === "-"
      ? product.product_name
      : product.high_rank_company_name + "/" + product.product_name;
  });

  useEffect(() => {
    let product_id: null | number = null;
    let product_key: string = "";
    productList.map((product) => {
      const _postCSVProductName =
        product.high_rank_company_name === "-"
          ? product.product_name
          : product.high_rank_company_name + "/" + product.product_name;
      if (postCSVProductName === _postCSVProductName) {
        product_id = product.id;
        product_key = product.product_key;
      }
    });
    if (product_key === "申込番号") {
      setPostCSVColumn(putCSVColumnContractNumber);
    } else if (product_key === "顧客電話番号") {
      setPostCSVColumn(putCSVColumnPhoneNumber);
    }
    setPostCSVStatus({
      ...postCSVStatus,
      product_id,
      product_key,
    });
  }, [postCSVProductName]);

  const [onPostBottun, setOnPostBottun] = useState(true);
  useEffect(() => {
    let onPostBottun = true;
    let putCSVColumn =
      postCSVStatus.product_key === "申込番号"
        ? putCSVColumnContractNumber
        : postCSVStatus.product_key === "顧客電話番号"
        ? putCSVColumnPhoneNumber
        : [];
    let putCSVColumnResult: string[] = [];
    const putCSVColumnResultAll =
      postCSVStatus.product_key === "申込番号"
        ? putCSVColumnResultContractNumber
        : postCSVStatus.product_key === "顧客電話番号"
        ? putCSVColumnResultPhoneNumber
        : [];
    postCSVStatusHeader.map((csvHeader) => {
      if (
        postCSVStatus.product_key === "申込番号" &&
        postCSVStatus.header_inf[csvHeader] === "申込番号"
      ) {
        onPostBottun = false;
      } else if (
        postCSVStatus.product_key === "顧客電話番号" &&
        postCSVStatus.header_inf[csvHeader] === "顧客電話番号"
      ) {
        onPostBottun = false;
      }
      putCSVColumn = putCSVColumn.filter((column) => {
        return (
          column !== postCSVStatus.header_inf[csvHeader] ||
          column === "顧客・契約情報" ||
          column === "取込を行わない"
        );
      });

      // CSV取込後のエラー表示時の項目
      if (putCSVColumnResultAll.includes(postCSVStatus.header_inf[csvHeader])) {
        putCSVColumnResult.push(postCSVStatus.header_inf[csvHeader]);
      }
    });
    setPostCSVColumn(putCSVColumn);
    setOnPostBottun(onPostBottun);
    setPostCSVColumnResult(putCSVColumnResult);
  }, [postCSVStatus]);

  async function downloadErrorList() {
    setPostCSVStatus({
      ...postCSVStatus,
      status: "loading",
    });
    const header_location = postCSVStatus.header_location;
    if (header_location != "None") {
      const url = header_location;
      const link = document.createElement("a");
      link.href = header_location;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setPostCSVStatus({
      ...postCSVStatus,
      status: "result",
    });
  }

  // ★cmd-po_1142: footer を status に応じて切り替える
  const footerNode =
    (postCSVStatus.status === "posting" || postCSVStatus.status === "loading") ? undefined :
    postCSVStatus.status === "open" ? (
      <>
        <PoButton
          variant="secondary"
          data-testid="csv-contract-status-cancel-button"
          onClick={() => {
            swCloseModal();
            setPostCSVProductName("");
          }}
        >
          キャンセル
        </PoButton>
        <Box
          component="input"
          disabled={
            postCSVStatus.product_id === null &&
            postCSVStatus.product_key === ""
          }
          accept=".csv"
          sx={{ display: "none" }}
          id="raised-button-file-contract"
          type="file"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e)}
        />
        <label htmlFor="raised-button-file-contract">
          <PoButton
            data-testid="csv-contract-status-upload-button"
            disabled={
              postCSVStatus.product_id === null &&
              postCSVStatus.product_key === ""
            }
            component="span"
          >
            CSV読取
            <AttachFileIcon
              sx={{ marginTop: "-2.5px", marginLeft: "6px" }}
              fontSize="medium"
            />
          </PoButton>
        </label>
      </>
    ) : postCSVStatus.status === "column" ? (
      <>
        <PoButton
          variant="secondary"
          data-testid="csv-contract-status-back-button"
          onClick={() => {
            setPostCSVStatus({
              ...postCSVStatus,
              status: "open",
            });
          }}
        >
          戻る
        </PoButton>
        <PoButton
          disabled={onPostBottun}
          data-testid="csv-contract-status-start-button"
          onClick={startPutCSVFunc}
        >
          取込開始
        </PoButton>
      </>
    ) : postCSVStatus.status === "error" ? (
      <PoButton
        variant="secondary"
        data-testid="csv-contract-status-error-back-button"
        onClick={() => {
          setPostCSVStatus({
            status: "open",
            message: "",
            result: [],
            header_location: "None",
            destination_key: "",
            product_id: null,
            product_key: "",
            header_inf: {},
            error_count: null,
            total_count: null,
            success_count: null,
          });
        }}
      >
        戻る
      </PoButton>
    ) : postCSVStatus.status === "result" ? (
      <>
        {postCSVStatus.header_location != "None" ? (
          <PoButton
            variant="secondary"
            sx={{ mr: "auto" }}
            data-testid="csv-contract-status-error-download"
            onClick={downloadErrorList}
          >
            エラー内容ダウンロード
          </PoButton>
        ) : null}
        <PoButton
          data-testid="csv-contract-status-close-button"
          onClick={() => {
            swCloseModal();
            setPostCSVProductName("");
          }}
        >
          閉じる
        </PoButton>
      </>
    ) : null;

  return (
    <PoModalShell
      open={onModal}
      title={postCSVTitle}
      onClose={swCloseModal}
      frameTestId="csv-contract-status-modal"
      testidPrefix="csv-contract-status"
      widthPx={dialogWidth}
      bodySx={postCSVStatus.status === "column" ? { pt: "var(--sp-4, 16px)", gap: "var(--sp-3, 12px)" } : undefined}
      footer={footerNode}
    >
      {postCSVStatus.status === "open" ? (
        <>
          {/* open 面: PoField + PoLabel(required) + Autocomplete + PoHelper */}
          <PoField fieldKey="product" testId="csv-contract-status-field-product">
            <PoLabel required>取込商材選択</PoLabel>
            <Autocomplete
              sx={poAutocompleteSx}
              clearIcon={null}
              options={autocompleteProductList}
              value={postCSVProductName}
              onChange={(event: any, newValue: string | null) => {
                if (newValue) {
                  setPostCSVProductName(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField value={postCSVProductName} {...params} />
              )}
            />
          </PoField>
          <PoHelper>CSVファイルを読み取り、ヘッダー行から取込項目の割り当てへ進みます</PoHelper>
          {/* ★cmd-po_1232 R33: 確認step廃止に伴い、データ行0など選択NG時のエラーを選択面に表示（床の移設） */}
          {fileError ? (
            <Alert severity="error" data-testid="csv-contract-status-file-error" sx={{ mt: "var(--sp-2, 8px)" }}>
              {fileError}
            </Alert>
          ) : null}
        </>
      ) : postCSVStatus.status === "column" ? (
        <>
          {/* column 面: pp-sec-title + PoHelper + map-table + cc-notes */}
          <Box
            data-testid="csv-contract-status-section-title"
            sx={{
              fontSize: "var(--fs-12, 12px)",
              fontWeight: "var(--fw-bold, 700)",
              color: "var(--color-ai, #1e3a5f)",
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--sp-2, 8px)",
            }}
          >
            CSV項目一覧
          </Box>
          <PoHelper>読み取ったCSVのヘッダーごとに、取込先の項目を選択してください</PoHelper>
          <TableContainer
            component={Paper}
            data-testid="csv-contract-status-map-table"
            sx={{ width: "100%", borderRadius: 1 }}
          >
            <Table
              size="small"
              sx={{ width: "100%", borderCollapse: "collapse" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: "var(--fs-11, 11px)",
                      fontWeight: "var(--fw-bold, 700)",
                      color: "var(--color-white)",
                      textAlign: "left",
                      padding: "var(--sp-2, 8px)",
                      borderBottom: "1px solid var(--color-border, #e5e5e0)",
                      bgcolor: "var(--color-ai)",
                    }}
                  >
                    CSVのヘッダー
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "var(--fs-11, 11px)",
                      fontWeight: "var(--fw-bold, 700)",
                      color: "var(--color-white)",
                      textAlign: "left",
                      padding: "var(--sp-2, 8px)",
                      borderBottom: "1px solid var(--color-border, #e5e5e0)",
                      bgcolor: "var(--color-ai)",
                      width: "28px",
                    }}
                  />
                  <TableCell
                    sx={{
                      fontSize: "var(--fs-11, 11px)",
                      fontWeight: "var(--fw-bold, 700)",
                      color: "var(--color-white)",
                      textAlign: "left",
                      padding: "var(--sp-2, 8px)",
                      borderBottom: "1px solid var(--color-border, #e5e5e0)",
                      bgcolor: "var(--color-ai)",
                    }}
                  >
                    取込先の項目
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {postCSVStatusHeader.map((header, idx) => {
                  return (
                    <TableRow key={header}>
                      <TableCell
                        sx={{
                          fontSize: "var(--fs-13, 13px)",
                          color: "var(--color-sumi, #374151)",
                          fontWeight: "var(--fw-semibold, 600)",
                          width: "36%",
                          padding: "var(--sp-2, 8px)",
                          borderBottom:
                            "1px solid var(--color-border-light, #efefea)",
                        }}
                      >
                        {header}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "28px",
                          color: "var(--color-sumi-muted, #9ca3af)",
                          textAlign: "center",
                          padding: "var(--sp-2, 8px)",
                          borderBottom:
                            "1px solid var(--color-border-light, #efefea)",
                        }}
                      >
                        <ArrowForwardIcon sx={{ fontSize: "14px" }} />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "var(--sp-2, 8px)",
                          borderBottom:
                            "1px solid var(--color-border-light, #efefea)",
                        }}
                      >
                        <PoField
                          fieldKey={header}
                          testId={"csv-contract-status-field-" + header}
                        >
                          <Autocomplete
                            sx={poAutocompleteSx}
                            clearIcon={null}
                            options={postCSVColumn}
                            value={postCSVStatus.header_inf[header]}
                            onChange={(
                              event: any,
                              newValue: string | null
                            ) => {
                              if (newValue) {
                                let header_inf = postCSVStatus.header_inf;
                                header_inf[header] = newValue;
                                setPostCSVStatus({
                                  ...postCSVStatus,
                                  header_inf,
                                });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                value={postCSVStatus.header_inf[header]}
                                {...params}
                              />
                            )}
                          />
                        </PoField>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* cc-notes */}
          <Box
            data-testid="csv-contract-status-notes"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--sp-05, 2px)",
              fontSize: "var(--fs-11, 11px)",
              color: "var(--color-sumi-muted, #9ca3af)",
            }}
          >
            <span>
              ※何も選択していないCSV項目は「取込を行わない」として処理されます
            </span>
            <span>
              {postCSVStatus.product_key === "申込番号"
                ? "※「申込番号」の選択は必須です"
                : postCSVStatus.product_key === "顧客電話番号"
                ? "※「顧客電話番号」の選択は必須です"
                : ""}
            </span>
          </Box>
        </>
      ) : (postCSVStatus.status === "posting" || postCSVStatus.status === "loading") ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Box sx={{ textAlign: "center" }}>{postCSVStatus.message}</Box>
        </Box>
      ) : postCSVStatus.status === "error" ? (
        <Alert severity="error">
          <Box>CSVの取込に失敗しました。</Box>
          {!isNonHeaderCsvError(postCSVStatus.message) && (
            <Box sx={{ marginTop: "15px" }}>
              ・質問名が異なる可能性があります。
            </Box>
          )}
          <Box sx={{ textAlign: "center", whiteSpace: "pre-line" }}>
            {postCSVStatus.message}
          </Box>
        </Alert>
      ) : postCSVStatus.status === "result" ? (
        <>
          <Box>{postCSVStatus.message}</Box>
          <Alert
            severity={
              postCSVStatus.header_location !== "None" ? "warning" : "success"
            }
            icon={<CheckCircleIcon />}
            sx={{ mb: 1.5 }}
            data-testid="csv-contract-status-result-banner"
          >
            {postCSVStatus.header_location !== "None"
              ? "CSV取込完了。エラー内容をダウンロードして確認してください。"
              : "CSV取込完了"}
          </Alert>
          {(() => {
            const errCnt = postCSVStatus.error_count != null ? postCSVStatus.error_count : postCSVStatus.result.filter((r) => r.message).length;
            const totalCnt = postCSVStatus.total_count != null ? postCSVStatus.total_count : rowCountSnapshotRef.current;
            const successCnt = postCSVStatus.success_count != null ? postCSVStatus.success_count : Math.max(0, totalCnt - errCnt);
            return (
              <div
                data-testid="csv-contract-status-result-counts"
                className="pp-sum"
              >
                <span
                  data-testid="csv-contract-status-result-counts-ok"
                  className="pp-sum-item ok"
                >
                  <span className="pp-sum-lbl">成功</span>
                  <span className="pp-sum-num">{successCnt}</span>
                </span>
                <span
                  data-testid="csv-contract-status-result-counts-ng"
                  className="pp-sum-item ng"
                >
                  <span className="pp-sum-lbl">失敗</span>
                  <span className="pp-sum-num">{errCnt}</span>
                </span>
              </div>
            );
          })()}
          {postCSVStatus.result.length > 0 ? (
            <Box sx={{ marginTop: "15px", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
              <Alert severity="error">
                ※以下データ登録時にエラーが発生しました。
              </Alert>
              <TableContainer
                component={Paper}
                data-testid="csv-contract-status-result-table"
                sx={{
                  margin: "1px 1px",
                  width: "calc(100% - 4px)",
                  flex: "1 1 auto",
                  minHeight: 0,
                  border: "1px solid rgb(230, 230, 230)",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  aria-label="collapsible table"
                  sx={{ minWidth: "max-content" }}
                >
                  <TableHead>
                    <TableRow key="errorCSVTableRow">
                      <TableCell
                        key={"errorCSVTableMessage"}
                        sx={{
                          fontSize: "var(--fs-11, 11px)",
                          fontWeight: "var(--fw-bold, 700)",
                          color: "var(--color-white)",
                          textAlign: "left",
                          padding: "var(--sp-2, 8px)",
                          borderBottom:
                            "1px solid var(--color-border, #e5e5e0)",
                          bgcolor: "var(--color-ai)",
                          minWidth: "220px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        エラーメッセージ
                      </TableCell>
                      {postCSVColumnResult.map((column, i) => {
                        return (
                          <TableCell
                            key={"errorCSVTableColumn" + i}
                            sx={{
                              fontSize: "var(--fs-11, 11px)",
                              fontWeight: "var(--fw-bold, 700)",
                              color: "var(--color-white)",
                              textAlign: "left",
                              padding: "var(--sp-2, 8px)",
                              borderBottom:
                                "1px solid var(--color-border, #e5e5e0)",
                              bgcolor: "var(--color-ai)",
                              minWidth: "120px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {column}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {postCSVStatus.result.map((result, i) => (
                      <Fragment key={"errorCSVTableFragment" + i}>
                        <TableRow>
                          <TableCell
                            key={"errorCSVTableCell" + i}
                            sx={{
                              fontSize: "var(--fs-13, 13px)",
                              color: "var(--color-sumi, #374151)",
                              padding: "var(--sp-2, 8px)",
                              borderBottom:
                                "1px solid var(--color-border-light, #efefea)",
                              minWidth: "220px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {result.message}
                          </TableCell>
                          {postCSVColumnResult.map((column, column_i) => {
                            return (
                              <TableCell
                                key={
                                  "errorCSVTableColumn" +
                                  i +
                                  "Data" +
                                  column_i
                                }
                                sx={{
                                  fontSize: "var(--fs-13, 13px)",
                                  color: "var(--color-sumi, #374151)",
                                  padding: "var(--sp-2, 8px)",
                                  borderBottom:
                                    "1px solid var(--color-border-light, #efefea)",
                                  minWidth: "120px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {result.postData[column]}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </>
      ) : null}
    </PoModalShell>
  );
}
